import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Store, TrendingUp, Truck, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  "Grocery & Staples",
  "Personal Care",
  "Baby Care",
  "Beverages",
  "Snacks & Biscuits",
  "Home & Kitchen",
  "Pharmacy",
  "Pooja Needs",
  "Other",
];

const perks = [
  { icon: TrendingUp, title: "Grow your revenue", desc: "Get discovered by thousands of nearby customers ordering online." },
  { icon: Truck, title: "We handle logistics", desc: "Zeroomile riders pick up and deliver — you focus on your store." },
  { icon: Sparkles, title: "Powered by technology", desc: "Inventory, orders & payments, all managed from one simple app." },
];

const initial = {
  store_name: "",
  owner_name: "",
  phone: "",
  email: "",
  city: "",
  pincode: "",
  category: "",
  message: "",
};

export const BecomeBrightspot = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.store_name || !form.owner_name || !form.phone || !form.city) {
      toast.error("Please fill in store name, owner, phone and city.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/brightspot-leads`, payload);
      setDone(true);
      setForm(initial);
      toast.success("Application received! Our team will reach out shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Something went wrong. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "mt-2 h-12 rounded-xl border-brand-line bg-brand-bg text-brand-bone placeholder:text-brand-muted/60 focus-visible:ring-brand-volt focus-visible:ring-offset-0";

  return (
    <section
      id="become-brightspot"
      data-testid="become-brightspot-section"
      className="relative overflow-hidden border-y border-brand-line bg-brand-surface/40 py-24 md:py-32"
    >
      <div className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-brand-volt/10 blur-[130px]" />

      <div className="relative mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-6 md:grid-cols-12 md:px-12">
        {/* Left — pitch */}
        <div className="md:col-span-5">
          <Reveal>
            <p className="mb-6 flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-brand-volt">
              <span className="h-px w-8 bg-brand-volt" /> For Local Sellers
            </p>
            <h2 className="font-display text-6xl uppercase leading-[0.9] tracking-tight md:text-7xl">
              Become a
              <br />
              <span className="text-brand-volt">Brightspot</span>
            </h2>
            <p className="mt-8 max-w-md text-lg leading-relaxed text-brand-muted">
              Turn your neighborhood store into a{" "}
              <span className="font-serif italic text-brand-bone">beacon of service</span>. Join the
              Zeroomile network and bring your shop online in days — no tech skills needed.
            </p>
          </Reveal>

          <div className="mt-10 space-y-6">
            {perks.map((p, i) => (
              <Reveal key={p.title} delay={0.1 + i * 0.08}>
                <div className="flex items-start gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-brand-line bg-brand-elevated text-brand-volt">
                    <p.icon size={18} />
                  </span>
                  <div>
                    <h3 className="font-medium text-brand-bone">{p.title}</h3>
                    <p className="mt-1 text-sm text-brand-muted">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Right — form */}
        <div className="md:col-span-7">
          <Reveal delay={0.1} y={50}>
            <div className="rounded-3xl border border-brand-line bg-brand-bg/70 p-7 backdrop-blur-xl md:p-10">
              {done ? (
                <motion.div
                  data-testid="lead-success"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-16 text-center"
                >
                  <CheckCircle2 size={56} className="text-brand-volt" />
                  <h3 className="mt-6 font-display text-4xl uppercase tracking-tight">
                    You&apos;re on the map!
                  </h3>
                  <p className="mt-3 max-w-sm text-brand-muted">
                    Thanks for your interest in becoming a Brightspot. Our partnerships team will
                    call you within 48 hours.
                  </p>
                  <button
                    data-testid="lead-another"
                    onClick={() => setDone(false)}
                    className="mt-8 rounded-full border border-brand-line px-6 py-3 text-xs uppercase tracking-[0.2em] text-brand-bone transition-colors duration-300 hover:border-brand-volt hover:text-brand-volt"
                  >
                    Submit another store
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="brightspot-form" className="space-y-5">
                  <div className="mb-2 flex items-center gap-3">
                    <Store className="text-brand-volt" size={20} />
                    <h3 className="text-xl font-medium text-brand-bone">Store application</h3>
                  </div>

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">
                        Store name *
                      </Label>
                      <Input
                        data-testid="lead-store-name"
                        value={form.store_name}
                        onChange={update("store_name")}
                        placeholder="e.g. Sharma Kirana Store"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">
                        Owner name *
                      </Label>
                      <Input
                        data-testid="lead-owner-name"
                        value={form.owner_name}
                        onChange={update("owner_name")}
                        placeholder="Your full name"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">
                        Phone *
                      </Label>
                      <Input
                        data-testid="lead-phone"
                        value={form.phone}
                        onChange={update("phone")}
                        placeholder="10-digit mobile"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">
                        Email
                      </Label>
                      <Input
                        data-testid="lead-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="you@example.com"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">
                        City *
                      </Label>
                      <Input
                        data-testid="lead-city"
                        value={form.city}
                        onChange={update("city")}
                        placeholder="e.g. Indore"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">
                        Pincode
                      </Label>
                      <Input
                        data-testid="lead-pincode"
                        value={form.pincode}
                        onChange={update("pincode")}
                        placeholder="452001"
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">
                      Primary category
                    </Label>
                    <Select
                      value={form.category}
                      onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
                    >
                      <SelectTrigger
                        data-testid="lead-category"
                        className="mt-2 h-12 rounded-xl border-brand-line bg-brand-bg text-brand-bone focus:ring-brand-volt"
                      >
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                      <SelectContent className="border-brand-line bg-brand-elevated text-brand-bone">
                        {categories.map((c) => (
                          <SelectItem key={c} value={c} data-testid={`lead-cat-${c}`}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-brand-muted">
                      Anything else?
                    </Label>
                    <Textarea
                      data-testid="lead-message"
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us about your store..."
                      rows={3}
                      className="mt-2 rounded-xl border-brand-line bg-brand-bg text-brand-bone placeholder:text-brand-muted/60 focus-visible:ring-brand-volt focus-visible:ring-offset-0"
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="lead-submit"
                    disabled={loading}
                    className="group relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-brand-volt px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-brand-bg transition-colors duration-300 hover:bg-brand-volt-hover disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" /> Submitting…
                      </>
                    ) : (
                      "Apply to join the network"
                    )}
                  </button>
                  <p className="text-center text-xs text-brand-muted">
                    No cost to apply · Our team reviews every application personally.
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
